import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  PRICING_FIELDS,
  bootstrapPickupHubsFromRestaurants,
  buildPricingPayload,
  emptyPricingForm,
  fetchGlobalPickupPricing,
  fetchHubPickupPricing,
  fetchPickupHubs,
  pricingRecordToForm,
  quotePickupLunchbox,
  saveGlobalPickupPricing,
  saveHubPickupPricing,
} from './pickupPricingApi';
import './EatsAdminCreatePage.css';
import './PickupPricingPage.css';

const money = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `₹${num.toFixed(2)}`;
};

const PickupPricingPage = () => {
  const [scope, setScope] = useState('global');
  const [hubs, setHubs] = useState([]);
  const [hubId, setHubId] = useState('');
  const [form, setForm] = useState(emptyPricingForm);
  const [effectiveNote, setEffectiveNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [quoteInputs, setQuoteInputs] = useState({
    distance_km: '5',
    route_type: 'SINGLE_HUB',
    after_cutoff: false,
  });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);

  const selectedHubLabel = useMemo(() => {
    if (!hubId) return '';
    const hub = hubs.find((h) => String(h.hub_id ?? h.hubId) === String(hubId));
    return hub?.hub_name || hub?.hubName || `Hub ${hubId}`;
  }, [hubId, hubs]);

  const loadHubs = useCallback(async () => {
    try {
      const rows = await fetchPickupHubs();
      setHubs(rows);
    } catch (e) {
      console.error('Failed to load pickup hubs', e);
      setHubs([]);
    }
  }, []);

  const handleBootstrapHubs = async () => {
    setBootstrapping(true);
    setError('');
    setMessage('');
    try {
      const result = await bootstrapPickupHubsFromRestaurants();
      const rows = Array.isArray(result?.hubs) ? result.hubs : await fetchPickupHubs();
      setHubs(rows);
      setMessage(
        result?.message ||
          `Created ${result?.created_count ?? 0} hub(s) from existing restaurants.`,
      );
      if (rows.length === 1) {
        setHubId(String(rows[0].hub_id ?? rows[0].hubId));
      }
    } catch (e) {
      console.error('Failed to bootstrap hubs', e);
      setError(
        e?.response?.data?.error_message ||
          e?.response?.data?.message ||
          e?.message ||
          'Failed to create hubs from restaurants.',
      );
    } finally {
      setBootstrapping(false);
    }
  };

  const loadPricing = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      if (scope === 'hub') {
        if (!hubId) {
          setForm(emptyPricingForm());
          setEffectiveNote('Select a hub to edit its override (falls back to global if none).');
          return;
        }
        const data = await fetchHubPickupPricing(hubId);
        if (data?.override) {
          setForm(pricingRecordToForm(data.override));
          setEffectiveNote(`Editing override for ${selectedHubLabel}.`);
        } else {
          setForm(pricingRecordToForm(data?.effective));
          setEffectiveNote(
            `No override yet for ${selectedHubLabel}. Form shows effective (global) values — save to create a hub override.`,
          );
        }
      } else {
        const data = await fetchGlobalPickupPricing();
        setForm(pricingRecordToForm(data));
        setEffectiveNote('Global default used when a hub has no override.');
      }
    } catch (e) {
      console.error('Failed to load pickup pricing', e);
      setError(
        e?.response?.data?.error_message ||
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load pickup pricing.',
      );
      setForm(emptyPricingForm());
    } finally {
      setLoading(false);
    }
  }, [scope, hubId, selectedHubLabel]);

  useEffect(() => {
    loadHubs();
  }, [loadHubs]);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const handleFieldChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = buildPricingPayload(form);
      let saved;
      if (scope === 'hub') {
        if (!hubId) {
          setError('Select a hub before saving an override.');
          return;
        }
        saved = await saveHubPickupPricing(hubId, payload);
        setMessage(`Hub pricing saved for ${selectedHubLabel}.`);
      } else {
        saved = await saveGlobalPickupPricing(payload);
        setMessage('Global pickup pricing saved.');
      }
      setForm(pricingRecordToForm(saved));
    } catch (e) {
      console.error('Failed to save pickup pricing', e);
      setError(
        e?.response?.data?.error_message ||
          e?.response?.data?.message ||
          e?.message ||
          'Failed to save pickup pricing.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleQuote = async () => {
    setQuoting(true);
    setError('');
    try {
      const distance = Number(quoteInputs.distance_km);
      if (!Number.isFinite(distance) || distance < 0) {
        setError('Quote distance must be a non-negative number.');
        return;
      }
      const payload = {
        distance_km: distance,
        route_type: quoteInputs.route_type,
        after_cutoff: Boolean(quoteInputs.after_cutoff),
      };
      if (scope === 'hub' && hubId) payload.hub_id = Number(hubId);
      const result = await quotePickupLunchbox(payload);
      setQuote(result);
    } catch (e) {
      console.error('Failed to quote pickup fee', e);
      setError(
        e?.response?.data?.error_message ||
          e?.response?.data?.message ||
          e?.message ||
          'Failed to calculate quote.',
      );
      setQuote(null);
    } finally {
      setQuoting(false);
    }
  };

  return (
    <div className="pickup-pricing-page">
      <p className="eats-admin-create-subtitle" style={{ marginTop: 0 }}>
        Local lunchbox fee: base + distance (after free km) + hub + school, plus transfer for Route B
        and optional cutoff surcharge.
      </p>

      <div className="pickup-pricing-toolbar">
        <div className="pickup-pricing-kind-tabs" role="tablist" aria-label="Pricing scope">
          <button
            type="button"
            role="tab"
            aria-selected={scope === 'global'}
            className={`pickup-pricing-kind-tab${scope === 'global' ? ' is-active' : ''}`}
            onClick={() => setScope('global')}
          >
            Global default
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={scope === 'hub'}
            className={`pickup-pricing-kind-tab${scope === 'hub' ? ' is-active' : ''}`}
            onClick={() => setScope('hub')}
          >
            Hub override
          </button>
        </div>
        {scope === 'hub' ? (
          <label className="pickup-pricing-field">
            <span>Hub</span>
            <select
              value={hubId}
              onChange={(e) => setHubId(e.target.value)}
            >
              <option value="">Select hub…</option>
              {hubs.map((hub) => {
                const id = hub.hub_id ?? hub.hubId;
                return (
                  <option key={id} value={id}>
                    {hub.hub_name || hub.hubName || `Hub ${id}`}
                    {hub.restaurant_id || hub.restaurantId
                      ? ` (R${hub.restaurant_id || hub.restaurantId})`
                      : ''}
                  </option>
                );
              })}
            </select>
          </label>
        ) : null}
      </div>

      {scope === 'hub' && hubs.length === 0 ? (
        <div className="pickup-pricing-note">
          No hubs yet. Create them from your active restaurants (`restaurant_t` → `pickup_hub_mt`).
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--create"
              onClick={handleBootstrapHubs}
              disabled={bootstrapping}
            >
              {bootstrapping ? 'Creating hubs…' : 'Create hubs from restaurants'}
            </button>
          </div>
        </div>
      ) : null}

      {effectiveNote ? <div className="pickup-pricing-note">{effectiveNote}</div> : null}
      {error ? <div className="eats-admin-error">{error}</div> : null}
      {message ? <div className="pickup-pricing-success">{message}</div> : null}

      {loading ? (
        <div className="pickup-pricing-note">Loading pricing…</div>
      ) : (
        <div className="pickup-pricing-grid">
          {PRICING_FIELDS.map(({ key, label, help }) => (
            <label key={key} className="pickup-pricing-field">
              <span>{label}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form[key]}
                onChange={(e) => handleFieldChange(key, e.target.value)}
              />
              <small>{help}</small>
            </label>
          ))}
          <label className="pickup-pricing-field">
            <span>Currency</span>
            <input
              type="text"
              maxLength={8}
              value={form.currency}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
            />
          </label>
          <label className="pickup-pricing-field pickup-pricing-field--check">
            <span>Active</span>
            <input
              type="checkbox"
              checked={form.is_active !== false}
              onChange={(e) => handleFieldChange('is_active', e.target.checked)}
            />
          </label>
        </div>
      )}

      <div className="pickup-pricing-actions">
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--primary"
          onClick={handleSave}
          disabled={saving || loading || (scope === 'hub' && !hubId)}
        >
          {saving ? 'Saving…' : 'Save pricing'}
        </button>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--secondary"
          onClick={loadPricing}
          disabled={loading}
        >
          Reload
        </button>
      </div>

      <section className="pickup-pricing-quote">
        <h3>Quote preview</h3>
        <div className="pickup-pricing-grid pickup-pricing-grid--quote">
          <label className="pickup-pricing-field">
            <span>Distance (km)</span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={quoteInputs.distance_km}
              onChange={(e) =>
                setQuoteInputs((c) => ({ ...c, distance_km: e.target.value }))
              }
            />
          </label>
          <label className="pickup-pricing-field">
            <span>Route type</span>
            <select
              value={quoteInputs.route_type}
              onChange={(e) =>
                setQuoteInputs((c) => ({ ...c, route_type: e.target.value }))
              }
            >
              <option value="SINGLE_HUB">Route A — SINGLE_HUB</option>
              <option value="HUB_TRANSFER">Route B — HUB_TRANSFER</option>
            </select>
          </label>
          <label className="pickup-pricing-field pickup-pricing-field--check">
            <span>After cutoff</span>
            <input
              type="checkbox"
              checked={Boolean(quoteInputs.after_cutoff)}
              onChange={(e) =>
                setQuoteInputs((c) => ({ ...c, after_cutoff: e.target.checked }))
              }
            />
          </label>
        </div>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--primary"
          onClick={handleQuote}
          disabled={quoting}
        >
          {quoting ? 'Calculating…' : 'Calculate quote'}
        </button>

        {quote ? (
          <div className="pickup-pricing-quote-result">
            <div className="pickup-pricing-quote-total">
              Total {money(quote.total)}
              <small>
                {' '}
                ({quote.currency || 'INR'}
                {quote.inputs?.billable_km != null
                  ? ` · ${quote.inputs.billable_km} billable km`
                  : ''}
                )
              </small>
            </div>
            <ul>
              {Object.entries(quote.breakdown || {}).map(([key, value]) => (
                <li key={key}>
                  <span>{key.replace(/_/g, ' ')}</span>
                  <strong>{money(value)}</strong>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default PickupPricingPage;
